import { createContext, useContext, useEffect, useState } from 'react';

export type PlatformContext = 'web' | 'sidebar' | 'desktop';

type PlatformContextValue = {
  platform: PlatformContext;
  isSidebar: boolean;
  isWeb: boolean;
  isDesktop: boolean;
  webAppUrl: string;
};

const PlatformContext = createContext<PlatformContextValue | null>(null);

/**
 * Detects the platform context where the app is running
 * Uses multiple detection methods for reliability:
 * 1. URL parameter ?context=sidebar (set by extension)
 * 2. Chrome extension runtime detection
 * 3. Window width fallback (sidebar is narrow)
 */
function detectPlatformContext(): PlatformContext {
  // Method 1: Check URL parameter (most reliable)
  const params = new URLSearchParams(window.location.search);
  const contextParam = params.get('context');
  if (contextParam === 'sidebar') {
    return 'sidebar';
  }

  // Method 2: Check if running in Chrome extension + narrow width
  const isExtension =
    typeof chrome !== 'undefined' &&
    chrome.runtime?.id !== undefined &&
    window.location.protocol === 'chrome-extension:';

  if (isExtension) {
    // Check window width to differentiate sidebar from popup
    const isNarrow = window.innerWidth < 500;
    if (isNarrow) {
      return 'sidebar';
    }
  }

  // Method 3: Desktop detection (electron)
  // @ts-expect-error - window.electron exists in desktop app
  if (typeof window.electron !== 'undefined') {
    return 'desktop';
  }

  // Default: web app
  return 'web';
}

export const PlatformProvider = ({ children }: { children: React.ReactNode }) => {
  const [platform, setPlatform] = useState<PlatformContext>(detectPlatformContext);
  const webAppUrl = import.meta.env.VITE_WEB_APP_URL || 'http://localhost:5173';

  // Re-detect on window resize (in case sidebar gets resized)
  useEffect(() => {
    const handleResize = () => {
      const newPlatform = detectPlatformContext();
      if (newPlatform !== platform) {
        setPlatform(newPlatform);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [platform]);

  const value: PlatformContextValue = {
    platform,
    isSidebar: platform === 'sidebar',
    isWeb: platform === 'web',
    isDesktop: platform === 'desktop',
    webAppUrl,
  };

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>;
};

export const usePlatform = () => {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within PlatformProvider');
  }
  return context;
};
